/* eslint-disable @typescript-eslint/no-explicit-any */
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation';
import type { SearchParams } from '@/server/searchParams.ts';
import { type SearchInfoDetailData } from '@/app/search/types.ts';
import { type Target } from '@/app/components/search/components/Form';
import type { CommonData } from '@/app/common/createCommonData.ts';
import { SearchModes } from '@/domainLogic/index.ts';
import { getSearchModeFromSearchParams } from '@/app/components/search/utils/query2.ts';
import { getFacilitiesDict } from '../../components/Filters/verticalFilters/Fields/getFacilitiesField/getFacilitiesOptions';
import { clientAppLog } from '../../logging/clientAppLog';
import { propertyTypes } from '../../components/Filters/verticalFilters/Fields/getPropertyTypesField/utils';
import FilterContainer from '../../components/Filters/verticalFilters/FilterContainer';
import { SOURCE } from '../../app';
import { ClickEventNameEnum } from '../../Curiosity/Analytics/utils/ClickEventNameEnum';

export const platformVersion = '1.0.0';

export enum Actions {
  DP = 'flight+hotel',
  HO = 'hotel',
  FF = 'flights',
  findMore = 'find_more_offers',
  showMore = 'show_more',
  freeCancellation = 'free_cancellation',
  seaFont = 'seafront',
  freeWifi = 'free_wifi',
  threePlusStars = '3+_stars',
  highGuestRating = 'high_guest_rating',
  sorting = 'sorting',
  allFilters = 'all_filters',
  flashSales = 'flash_sales',
  budgetSlider = 'budget-slider',
  flight = 'flight',
  stars = 'stars',
  mealPlan = 'meal_plan',
  guestRating = 'guest_rating',
  propertyType = 'accomodation_type',
  facilities = 'facilities',
  filters = 'filters',
  cardDP = 'card-dp',
  cardHO = 'card-hotel',
  boxMapDP = 'box-map-dp',
  boxMapHO = 'box-map-hotel',
}

export enum guestRating {
  excellent = 'excellent',
  very_good = 'very_good',
  good = 'good',
}

export enum Categories {
  searchWidget = 'search_widget',
  cta = 'cta',
  resultsFilters = 'results_filters',
  resultsList = 'results_list',
}

export enum InteractionLabel {
  click = 'click',
  open = 'open',
  selected = 'selected',
  deselected = 'deselected',
  search = 'search-',
  menu = 'change_tab-',
  directFlight = 'direct_flights_only_',
  excludeOvernight = 'exclude_overnight_flights_',
  apply = 'Apply',
  reset = 'Reset',
}

export const normalizeHorizontalFilter = (filterName: string, value?: any) => {
  switch (filterName) {
    case URLParameters.PRICE:
      return 'budget-slider';
    case FilterContainer.StarsContainer:
    case URLParameters.STARS:
      return 'stars';
    case FilterContainer.MealContainer:
    case URLParameters.MEAL:
      return 'meal_plan';
    case FilterContainer.RatingContainer:
    case URLParameters.RATING:
      return 'guest_rating';
    case FilterContainer.PropertyTypeContainer:
    case URLParameters.ACCOMODATION_TYPE:
      return 'accomodation_type';
    case FilterContainer.LeisureContainer:
    case URLParameters.LEISURE:
      return 'leisure';
    case FilterContainer.FacilitiesContainer:
    case URLParameters.ACCOMODATION_FACILITIES:
      return 'facilities';
    case URLParameters.FLASH_SALES:
      return 'flash_sales';
    case FilterContainer.FlightContainer:
    case URLParameters.DIRECT:
      return 'flight';
    case URLParameters.SORT:
      return `${Actions.sorting}-${value}`;
    default:
      return filterName;
  }
};

export const normalizeFilterLabel = (e: Target, appliedFilters: SearchParams, t: TranslationFunction) => {
  switch (e.target.name) {
    case URLParameters.SORT:
    case URLParameters.FREE_CANCELLATION:
    case URLParameters.BOOK_NOW_PAY_LATER:
    case URLParameters.FLASH_SALES:
      return e.target.value ? InteractionLabel.selected : InteractionLabel.deselected;
    case URLParameters.PRICE:
      return `${e.target.value.min}-${e.target.value.max}`;
    case URLParameters.DIRECT:
      return `${InteractionLabel.directFlight}${
        e.target.value ? InteractionLabel.selected : InteractionLabel.deselected
      }`;
    case FilterContainer.FlightContainer:
    case FilterContainer.FacilitiesContainer:
    case FilterContainer.StarsContainer:
    case FilterContainer.MealContainer:
    case FilterContainer.RatingContainer:
    case FilterContainer.LeisureContainer:
    case FilterContainer.AccommodationChainsContainer:
    case FilterContainer.AccommodationDistrictsContainer:
    case FilterContainer.AccommodationThemesContainer:
    case URLParameters.STARS:
    case URLParameters.MEAL:
    case URLParameters.RATING:
    case URLParameters.ACCOMODATION_TYPE:
    case URLParameters.ACCOMODATION_FACILITIES:
    case URLParameters.ACCOMODATION_CHAIN:
    case URLParameters.ACCOMODATION_THEME:
    case URLParameters.ACCOMODATION_DISTRICT:
    case URLParameters.ACCOMODATION_BEACH:
    case URLParameters.ACCOMODATION_SKILIFT:
      return multiSelectNormalization(appliedFilters, e, t);
    default:
      return null;
  }
};

export const normalizeFilterValue = (e: Target) => {
  if (!e.target.value) {
    return '';
  }
  switch (e.target.name) {
    case 'price':
      return `${e.target.value.min}-${e.target.value.max}`;
    default:
      return `${e.target.value}`;
  }
};

export const normalizeEvents = (filterName: FilterContainer | URLParameters | string) => {
  switch (filterName) {
    case FilterContainer.LeisureContainer:
      return {
        event_name: ClickEventNameEnum.FiltersLeisure,
        event_subcontext: 'clear',
      };
    case FilterContainer.FlightContainer:
      return {
        event_name: 'flight',
        event_subcontext: 'clear',
      };
    case FilterContainer.FacilitiesContainer:
      return {
        event_name: ClickEventNameEnum.FiltersFacilities,
        event_subcontext: 'clear',
      };
    case FilterContainer.StarsContainer:
      return {
        event_name: ClickEventNameEnum.FiltersHotelStars,
        event_subcontext: 'clear',
      };
    case FilterContainer.MealContainer:
      return {
        event_name: ClickEventNameEnum.FiltersMeal,
        event_subcontext: 'clear',
      };
    case FilterContainer.RatingContainer:
      return {
        event_name: ClickEventNameEnum.FiltersRating,
        event_subcontext: 'clear',
      };
    case URLParameters.FREE_CANCELLATION:
      return {
        event_name: ClickEventNameEnum.FiltersFreecancellation,
        event_subcontext: 'checkbox',
      };
    case URLParameters.BOOK_NOW_PAY_LATER:
      return {
        event_name: ClickEventNameEnum.BookNowPayLater,
        event_subcontext: 'checkbox',
      };
    case URLParameters.DIRECT:
      return {
        event_name: ClickEventNameEnum.FiltersDirect,
        event_subcontext: 'checkbox',
      };
    case URLParameters.FLASH_SALES:
      return {
        event_name: ClickEventNameEnum.FiltersFlashSales,
        event_subcontext: 'checkbox',
      };
    case URLParameters.PINK_PEOPLE:
      return {
        event_name: ClickEventNameEnum.FiltersPinkPeople,
        event_subcontext: 'checkbox',
      };
    case URLParameters.PRICE:
      return {
        event_name: ClickEventNameEnum.FiltersPrice,
        event_subcontext: 'range',
      };
    case URLParameters.STARS:
      return {
        event_name: ClickEventNameEnum.FiltersHotelStars,
        event_subcontext: 'checkbox_list',
      };
    case URLParameters.MEAL:
      return {
        event_name: ClickEventNameEnum.FiltersMeal,
        event_subcontext: 'checkbox_list',
      };
    case URLParameters.RATING:
      return {
        event_name: ClickEventNameEnum.FiltersRating,
        event_subcontext: 'checkbox_list',
      };
    case URLParameters.ACCOMODATION_TYPE:
      return {
        event_name: ClickEventNameEnum.FiltersPropertytypes,
        event_subcontext: 'checkbox_list',
      };
    case FilterContainer.PropertyTypeContainer:
      return {
        event_name: ClickEventNameEnum.FiltersPropertytypes,
        event_subcontext: 'checkbox_list',
      };
    case 'propertyTypes':
      return {
        event_name: ClickEventNameEnum.FiltersPropertytypes,
        event_subcontext: 'checkbox_list',
      };
    case URLParameters.ACCOMODATION_FACILITIES:
    case URLParameters.GENERAL_ACCOMODATION_FACILITIES:
    case URLParameters.FAMILY_ACCOMODATION_FACILITIES:
    case URLParameters.ACTIVITY_ACCOMODATION_FACILITIES:
    case URLParameters.WELLNESS_ACCOMODATION_FACILITIES:
    case URLParameters.FOOD_ACCOMODATION_FACILITIES:
    case URLParameters.FACILITIES:
      return {
        event_name: ClickEventNameEnum.FiltersFacilities,
        event_subcontext: 'checkbox_list',
      };
    case URLParameters.SORT:
      return {
        event_name: ClickEventNameEnum.sortClick,
        event_subcontext: 'select',
      };
    case URLParameters.LEISURE:
      return {
        event_name: ClickEventNameEnum.FiltersLeisure,
        event_subcontext: 'checkbox_list',
      };
    case URLParameters.BLUE_FLAG:
      return {
        event_name: ClickEventNameEnum.FiltersBlueFlag,
        event_subcontext: 'checkbox',
      };
    case URLParameters.SUSTAINABILITY_CERTIFICATE:
      return {
        event_name: ClickEventNameEnum.FiltersSustainabilityCertificate,
        event_subcontext: 'checkbox',
      };
    case URLParameters.FAUNA:
      return {
        event_name: ClickEventNameEnum.FiltersFauna,
        event_subcontext: 'checkbox_list',
      };
    case FilterContainer.BeachTypeContainer:
    case URLParameters.BEACH_TYPE:
      return {
        event_name: ClickEventNameEnum.FiltersBeachType,
        event_subcontext: 'checkbox_list',
      };
    case URLParameters.PRE_FILTER_DESTINATION_AIRPORT:
      return {
        event_name: 'filters_prefilter_destination_airport',
        event_subcontext: 'radio_list',
      };
    case URLParameters.OVERNIGHT_FLIGHTS:
      return {
        event_name: 'filters_overnight_selected',
        event_subcontext: 'radio_list',
      };
    case URLParameters.CLIMATE:
      return {
        event_name: ClickEventNameEnum.FiltersClimate,
        event_subcontext: 'radio_list',
      };
    case URLParameters.WATERSPORT:
      return {
        event_name: ClickEventNameEnum.FiltersWatersport,
        event_subcontext: 'checkbox_list',
      };
    case FilterContainer.EntertainmentsContainer:
    case URLParameters.ENTERTAINMENTS:
      return {
        event_name: ClickEventNameEnum.FiltersEntertainments,
        event_subcontext: 'checkbox_list',
      };
    case FilterContainer.WaterwavesContainer:
    case URLParameters.WATER_WAVES:
      return {
        event_name: ClickEventNameEnum.FiltersWaterwaves,
        event_subcontext: 'checkbox_list',
      };
    case URLParameters.TOP_SECRET_HOTEL:
      return {
        event_name: ClickEventNameEnum.FiltersTsh,
        event_subcontext: 'checkbox',
      };
    case FilterContainer.AccommodationChainsContainer:
    case URLParameters.ACCOMODATION_CHAIN:
      return {
        event_name: `filters_${URLParameters.ACCOMODATION_CHAIN}`,
        event_subcontext: 'checkbox_list',
      };
    case FilterContainer.AccommodationBeachContainer:
    case URLParameters.ACCOMODATION_BEACH:
      return {
        event_name: `filters_${URLParameters.ACCOMODATION_BEACH}`,
        event_subcontext: 'checkbox_list',
      };
    case FilterContainer.AccommodationSkiliftContainer:
    case URLParameters.ACCOMODATION_SKILIFT:
      return {
        event_name: `filters_${URLParameters.ACCOMODATION_SKILIFT}`,
        event_subcontext: 'checkbox_list',
      };
    case FilterContainer.AccommodationDistrictsContainer:
    case URLParameters.ACCOMODATION_DISTRICT:
      return {
        event_name: `filters_${URLParameters.ACCOMODATION_DISTRICT}`,
        event_subcontext: 'checkbox_list',
      };
    case FilterContainer.AccommodationStationsContainer:
    case URLParameters.ACCOMODATION_STATION:
      return {
        event_name: `filters_${URLParameters.ACCOMODATION_STATION}`,
        event_subcontext: 'checkbox_list',
      };
    case FilterContainer.AccommodationThemesContainer:
    case URLParameters.ACCOMODATION_THEME:
      return {
        event_name: `filters_${URLParameters.ACCOMODATION_THEME}`,
        event_subcontext: 'checkbox_list',
      };
    case URLParameters.ACCOMODATION_NAME:
      return {
        event_name: `filters_${URLParameters.ACCOMODATION_NAME}`,
        event_subcontext: 'search',
      };
    case URLParameters.MY_PRICE:
      return {
        event_name: `filters_${URLParameters.MY_PRICE}`,
        event_subcontext: 'search',
      };
    default:
      clientAppLog({
        level: 'ERROR',
        codeSourceName: 'Analytics:utils:index::normalizeEvents',
        logMessage: `filterName not mapped: ${filterName}`,
        url: window?.location?.href,
      });
      return {
        event_name: '',
      };
  }
};

/* TODO: CHECK FILTERS NAME CONVERSION */
const convertName = (name: string) => {
  switch (name) {
    case FilterContainer.LeisureContainer:
      return 'leisure';
    case FilterContainer.FlightContainer:
      return 'flight';
    case FilterContainer.FacilitiesContainer:
      return 'facilities';
    case FilterContainer.RatingContainer:
      return 'rating';
    case FilterContainer.MealContainer:
      return 'meal';
    case FilterContainer.StarsContainer:
      return 'hotel_stars';
    case FilterContainer.PropertyTypeContainer:
      return 'accomodation_type';
    default:
      return name;
  }
};

enum MealPlan {
  RoomOnly = 'room_only',
  FullBoard = 'full_board',
  Breakfast = 'breakfast',
  HalfBoard = 'half_board',
  AllInclusive = 'all_inclusive',
}

const convertMealPlan = (value: string) => {
  switch (value) {
    case '1':
      return MealPlan.RoomOnly;
    case '2':
      return MealPlan.Breakfast;
    case '3':
      return MealPlan.HalfBoard;
    case '4':
      return MealPlan.FullBoard;
    case '5':
      return MealPlan.AllInclusive;
    default:
      return null;
  }
};

export const multiSelectNormalization = (appliedFilters: SearchParams, e: Target, t: TranslationFunction) => {
  const name = convertName(e.target.name);
  const value = e.target.value && e.target.value.map((x: string | number) => x.toString());
  const isClear = e.target.clear;

  if (!appliedFilters[name]) {
    if (isClear) return null;
    let addValue = value;
    if (name === URLParameters.MEAL) {
      addValue = convertMealPlan(value.toString());
    } else if (name === URLParameters.ACCOMODATION_TYPE) {
      addValue = propertyTypes(t).filter((x) => x.id === parseInt(value.toString(), 10))[0]?.analytics;
    } else if (name === URLParameters.ACCOMODATION_FACILITIES) {
      addValue = getFacilitiesDict(t).filter((x) => x.id === parseInt(value.toString(), 10))[0]?.analytics;
    }
    return `${addValue}_${InteractionLabel.selected}`;
  } else if (isClear) {
    return `${appliedFilters[name]}_${InteractionLabel.deselected}`;
  } else if (appliedFilters[name].split(',').length < value.length) {
    const appliedArray = appliedFilters[name].split(',').map((x) => x);
    let missing = value.filter((filterItem: string) => !appliedArray.includes(filterItem))[0];
    if (missing) {
      if (name === URLParameters.MEAL) {
        missing = convertMealPlan(missing);
      } else if (name === URLParameters.ACCOMODATION_TYPE) {
        missing = propertyTypes(t).filter((x) => x.id === parseInt(missing.toString(), 10))[0]?.analytics;
      } else if (name === URLParameters.ACCOMODATION_FACILITIES) {
        missing = getFacilitiesDict(t).filter((x) => x.id === parseInt(missing.toString(), 10))[0]?.analytics;
      }

      return `${missing}_${InteractionLabel.selected}`;
    }
    return null;
  } else {
    const appliedArray = appliedFilters[name].split(',').map((x) => x);
    const targetValues = value;
    let missing = appliedArray.filter((appliedItem) => !targetValues.includes(appliedItem))[0];
    if (missing) {
      if (name === URLParameters.MEAL) {
        missing = convertMealPlan(missing) || '';
      } else if (name === URLParameters.ACCOMODATION_TYPE) {
        missing = propertyTypes(t).filter((x) => x.id === parseInt(missing.toString(), 10))[0]?.analytics;
      } else if (name === URLParameters.ACCOMODATION_FACILITIES) {
        missing = getFacilitiesDict(t).filter((x) => x.id === parseInt(missing.toString(), 10))[0]?.analytics;
      }
      return `${missing}_${InteractionLabel.deselected}`;
    }
    return null;
  }
};

export enum AnalyticsEventType {
  destinationsResults = 'hdp.destResultsGrouped',
  results = 'hdp.results',
  noResults = 'hdp.noResults',
}

const parseDate = (str: string) => {
  const yyyymmdd = str.split('-');
  return new Date(parseInt(yyyymmdd[0], 10), parseInt(yyyymmdd[1], 10) - 1, parseInt(yyyymmdd[2], 10));
};

export const getDuration = ({ dateFrom, dateTo }: { dateFrom?: string; dateTo?: string }): string | number => {
  if (!dateFrom || !dateTo) return '';
  if (dateTo.indexOf('T') !== -1) {
    const daysArray = dateTo.split(',');
    return `${daysArray[1]}-${daysArray[2]}`;
  } else if (dateTo.indexOf('W') !== -1) {
    return `2-2`;
  } else {
    const departureDate = parseDate(dateFrom).valueOf();
    const returnDate = parseDate(dateTo).valueOf();
    return Math.round((returnDate - departureDate) / (1000 * 60 * 60 * 24));
  }
};

export const getSource = (source: string): string => {
  switch (source) {
    case SOURCE.OSE_RESULTS_ABBA:
      return 'abba';
    case SOURCE.CSW:
      return 'widget_csw';
    case SOURCE.BANNER:
    case SOURCE.PODS:
      return 'offers_merch';
    case SOURCE.PODS_TSX:
      return 'offers_tsx';
    default:
      return 'widget_tsx';
  }
};

enum OriginType {
  CityId = 'cityId',
  AirportCode = 'airportCode',
}

export const getOriginData = ({
  origin,
  searchInfoDetailData,
}: {
  origin: any;
  searchInfoDetailData: SearchInfoDetailData;
}) => {
  const originMacroCode = searchInfoDetailData?.origin?.cityId || '';
  const originName = searchInfoDetailData?.origin?.description || '';
  const isOriginCity = searchInfoDetailData?.origin?.cityId ? searchInfoDetailData?.origin?.cityId === origin : false;
  const originType = isOriginCity ? OriginType.CityId : OriginType.AirportCode;

  return {
    originCode: origin,
    originMacroCode,
    originName,
    originType,
  };
};

type AnalyticsDq = {
  dq: {
    active: boolean;
    ipName?: string;
    ipType?: string;
    ipCategory?: string;
  };
};

export const createUserDqData = (commonData: CommonData): AnalyticsDq | undefined => {
  if (!commonData?.casperResponseProperties?.dq) return undefined;
  const { ipName, ipType, ipCategory } = commonData.casperResponseProperties.dq;
  // appfw v6: `active` was removed from CasperResponseDq. It's now derived: a
  // non-empty `dq` object means Data Quality is active for this (internal) user.
  const active = Object.keys(commonData.casperResponseProperties.dq).length > 0;
  const ipNameVal = ipName ? { ipName } : undefined;
  const ipTypeVal = ipType ? { ipType } : undefined;
  const ipCategoryVal = ipCategory ? { ipCategory } : undefined;

  return {
    dq: {
      active,
      ...ipNameVal,
      ...ipTypeVal,
      ...ipCategoryVal,
    },
  };
};

export const createUserDeviceType = (fallbackDeviceType: string, commonData: CommonData): string => {
  const casperDeviceType = commonData.casperResponseProperties?.userBrowserData?.deviceDescription?.type;
  const deviceType = casperDeviceType || fallbackDeviceType || '';
  return deviceType.toLowerCase();
};

export const registerEvents = (data: any) => {
  if ((window as Window).platformData) (window as Window).platformData.push(data);
  if ((window as Window).utag_data) (window as Window).utag_data = { ...(window as any).utag_data, ...data };
};

export const updateLastEvent = (eventName: string, data: any): void => {
  if (!(window as Window).platformData) {
    (window as Window).platformData = [];
  }
  const { platformData } = window as Window;

  const lastIndex = platformData.map((eventObj: any) => eventObj.event).lastIndexOf(eventName);

  if (lastIndex !== -1) {
    platformData[lastIndex] = data;
  } else {
    registerEvents(data);
  }
};

export const createPageCategory = (searchParams: SearchParams): string => {
  const searchMode = getSearchModeFromSearchParams(searchParams);
  return searchMode === SearchModes.HO ? 'hotel' : 'dp';
};
